import React, { useState, useEffect, createRef } from 'react'
import { Menu, Button, Popup, Image } from 'semantic-ui-react'
import ModuleAccordion from './ModuleAccordion'
import OspinModal, { UserDetailsInput, SaveDeviceResults } from './OspinModal'
import logo from '../ospin.png'
import { API_LINK } from '../config/config'
import { DeviceData } from '../lib/ConfiguratorGrid'

export type MenuProps = {
  zoomOut: () => void;
  zoomIn: () => void;
  closeContextMenu: () => void;
  getDevice: () => Promise<DeviceData> | undefined;
  showEntireDevice: () => void;
}

const ModuleMenu: React.FC<MenuProps> = ({
  zoomOut,
  zoomIn,
  closeContextMenu,
  showEntireDevice,
  getDevice,
}: MenuProps) => {
  const size = 'large'
  const [toggleClose, setToggleClose] = useState(false)
  const [loading, setLoading] = useState(false)
  const [email, setEmail] = useState('')
  const [name, setName] = useState<string | undefined>(undefined)
  const [deviceName, setDeviceName] = useState<string | undefined>(undefined)
  const [submitable, setSubmitable] = useState(false)
  const [agreeToShareData, setAgreeToShareData] = useState(false)
  const [saveMsg, setSaveMsg] = useState({ hidden: true, isError: false, msg: '' })
  const popupDisplayRef: React.RefObject<HTMLDivElement> = createRef()
  const regExToCheckEmail = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/

  const isEmailValid = () => regExToCheckEmail.test(email)
  const isUserDetailsValid = () => {
    if (isEmailValid() && agreeToShareData) {
      setSubmitable(true)
    } else {
      setSubmitable(false)
    }
  }
  const hideMessage = () => {
    if (!toggleClose) {
      setSaveMsg({ ...saveMsg, hidden: true })
    }
  }
  useEffect(isUserDetailsValid, [email, agreeToShareData])
  useEffect(hideMessage, [toggleClose])

  const onToggleModal = (): void => {
    closeContextMenu()
    setToggleClose(!toggleClose)
  }

  const onNameChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setName(e.target.value)
  }

  const onEmailChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setEmail(e.target.value)
  }

  const onToggleShareData = (): void => {
    setAgreeToShareData(!agreeToShareData)
  }

  const onDeviceNameChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setDeviceName(e.target.value)
  }

  const resetForm = () => {
    setLoading(false)
    setDeviceName(undefined)
    setName(undefined)
    setEmail('')
    setAgreeToShareData(false)
  }

  const onSendDeviceDetails = async () => {
    const data = await getDevice()
    if (data) {
      setLoading(true)
      const { imageData, configuration } = data
      const response = await fetch(API_LINK, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userName: name,
          deviceName,
          userEmail: email,
          configurator: configuration,
          image: imageData,
        }),
      })

      await response.json().then(res => {
        const { result } = res
        if (result !== 'Ok') {
          resetForm()
          setSaveMsg({ hidden: false, isError: true, msg: 'Something went wrong!' })
          return
        }
        resetForm()
        setSaveMsg({ hidden: false, isError: false, msg: 'Check your email!' })
      })

    }
  }

  return (
    <>
      <Menu secondary vertical style={{ width: '100%', height: '100%' }}>
        <Menu.Item
          name='viewControls'
        >
          <div
            ref={popupDisplayRef}
            style={{
              width: '5rem',
              height: '5rem',
              display: 'block',
              margin: '10px auto 10px',
            }}
          >
            <Image
              src={logo}
            />
          </div>
          <div
            style={{
              width: '100% ',
              display: 'flex',
              flexWrap: 'wrap',
              alignContent: 'space-between',
              justifyContent: 'center',
            }}
          >
            <Popup
              content='Zoom In'
              trigger={(
                <Button
                  size={size}
                  color='blue'
                  icon='plus'
                  circular
                  onClick={zoomIn}
                />
              )}
            />
            <Popup
              content='Show entire device'
              trigger={(
                <Button
                  size={size}
                  color='green'
                  icon='expand arrows alternate'
                  circular
                  onClick={showEntireDevice}
                />
              )}
            />
            <OspinModal
              submitable={submitable}
              submitionIsLoading={loading}
              onSubmit={onSendDeviceDetails}
              onToggleModal={onToggleModal}
              open={toggleClose}
              header='Send Me Device'
              toggle={(
                <Popup
                  content='Send device to your email'
                  trigger={(
                    <Button
                      size={size}
                      color='green'
                      icon='mail'
                      circular
                      onClick={onToggleModal}
                    />
                  )}
                />
              )}
            >
              <SaveDeviceResults isError={saveMsg.isError} hideMessage={saveMsg.hidden} message={saveMsg.msg} />
              <UserDetailsInput
                email={email}
                name={name}
                setAgreeToShareData={onToggleShareData}
                deviceName={deviceName}
                handleName={onNameChange}
                handleEmail={onEmailChange}
                handleDeviceName={onDeviceNameChange}
              />
            </OspinModal>
            <Popup content='Zoom Out' trigger={<Button size={size} color='blue' icon='minus' circular onClick={zoomOut} />} />
          </div>
        </Menu.Item>
        <Menu.Item>
          <ModuleAccordion popupDisplayRef={popupDisplayRef} />
        </Menu.Item>
      </Menu>
    </>
  )
}

export default ModuleMenu
